import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Check,
  Clock3,
  Download,
  History,
  Library,
  LogOut,
  Plus,
  Search,
  Sparkles,
} from 'lucide-react'
import { AI_ROLES } from '../lib/ai'
import { fullDate, projectProgress, projectWords, relativeTime } from '../lib/utils'
import { Avatar, CoverArt, LogoMark } from './Shared'
import NewProjectModal from './NewProjectModal'

const FILTERS = [
  { id: 'all', label: '全部' },
  { id: 'novel', label: '小说' },
  { id: 'research', label: '研究' },
  { id: 'essay', label: '随笔' },
]

function categoryOf(project) {
  if (project.type === '写作研究') return 'research'
  if (project.type === '私人手记' || project.type === '随笔') return 'essay'
  return 'novel'
}

function greeting() {
  const hour = new Date().getHours()
  if (hour < 5) return '夜深了'
  if (hour < 11) return '早上好'
  if (hour < 13) return '中午好'
  if (hour < 18) return '下午好'
  return '晚上好'
}

const SAVE_LABELS = {
  saving: '同步中',
  saved: '已同步',
  offline: '离线未同步',
  error: '同步失败',
  local: '本地模式',
}

export default function Home({ projects, onOpenProject, onCreateProject, onOpenRoundtable, notify, userEmail = null, saveState = 'saved', onLogout = null, onOpenHistory = null }) {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [creating, setCreating] = useState(false)
  const [installPrompt, setInstallPrompt] = useState(null)
  const [installed, setInstalled] = useState(false)
  const searchRef = useRef(null)
  const featured = projects[0]

  useEffect(() => {
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone
    setInstalled(Boolean(standalone))
    function onBeforeInstall(event) {
      event.preventDefault()
      setInstallPrompt(event)
    }
    function onInstalled() {
      setInstalled(true)
      setInstallPrompt(null)
      notify('页屿已安装到设备，可以离线打开。')
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [notify])

  useEffect(() => {
    function focusSearch(event) {
      if (event.key !== '/' || ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) return
      event.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', focusSearch)
    return () => window.removeEventListener('keydown', focusSearch)
  }, [])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return projects.filter((project) => {
      const categoryMatch = filter === 'all' || categoryOf(project) === filter
      if (!categoryMatch) return false
      if (!term) return true
      const cardText = project.cards.map((card) => card.title + ' ' + card.content).join(' ').toLowerCase()
      return (project.title + ' ' + project.subtitle + ' ' + cardText).toLowerCase().includes(term)
    })
  }, [projects, search, filter])

  const totalCards = projects.reduce((sum, project) => sum + project.cards.length, 0)
  const totalWords = projects.reduce((sum, project) => sum + projectWords(project), 0)
  const featuredProgress = featured ? projectProgress(featured) : 0

  async function installApp() {
    if (installPrompt) {
      await installPrompt.prompt()
      const choice = await installPrompt.userChoice
      if (choice?.outcome === 'accepted') setInstalled(true)
      setInstallPrompt(null)
      return
    }
    notify('请使用浏览器菜单中的“安装应用 / 添加到主屏幕”。在 iPhone 上请使用分享菜单。')
  }

  return (
    <div className="home">
      <aside className="rail">
        <div className="rail__brand" title="页屿 Page Isle">
          <LogoMark size={38} />
        </div>
        <nav className="rail__nav" aria-label="主导航">
          <button className="rail-link is-active" onClick={() => { setSearch(''); setFilter('all') }} title="我的书架">
            <Library size={19} />
            <span>书架</span>
          </button>
          <button
            className="rail-link"
            title="邀请 AI 圆桌"
            onClick={() => featured && onOpenRoundtable(featured.id)}
          >
            <Sparkles size={19} />
            <span>圆桌</span>
          </button>
        </nav>
        <div className="rail__bottom">
          <button
            className="rail-link rail-link--quiet"
            title="关于页屿"
            onClick={() => notify('页屿 · 登录后内容实时同步到云端，每次保存都会留下可回朔的版本。')}
          >
            <BookOpen size={18} />
            <span>关于</span>
          </button>
          <Avatar name={userEmail ? userEmail[0].toUpperCase() : '林'} size={36} />
        </div>
      </aside>

      <main className="home-main">
        <header className="home-top">
          <div className="home-brandline">
            <span className="home-brandline__name">页屿</span>
            <span className="home-brandline__divider" />
            <span className="home-brandline__en">PAGE ISLE</span>
            <span className="home-brandline__tag">私人书架</span>
          </div>
          <div className="home-tools">
            <label className="search-box">
              <Search size={16} />
              <input
                ref={searchRef}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="搜索笔记本、卡片或句子"
                aria-label="搜索笔记本、卡片或句子"
              />
              <kbd>/</kbd>
            </label>
            <div className="home-account">
              <span className={'sync-chip sync-chip--' + saveState} title="保存状态">{SAVE_LABELS[saveState] || '已同步'}</span>
              {onOpenHistory ? (
                <button className="btn btn--ghost" onClick={onOpenHistory} title="版本历史与回朔">
                  <History size={16} /> 历史
                </button>
              ) : null}
              {userEmail ? (
                <>
                  <span className="account-email" title={userEmail}>{userEmail}</span>
                  {onLogout ? (
                    <button className="btn btn--ghost" onClick={onLogout} title="退出登录">
                      <LogOut size={16} />
                    </button>
                  ) : null}
                </>
              ) : null}
            </div>
            <button className="btn btn--ink home-new" onClick={() => setCreating(true)}>
              <Plus size={17} /> 新建笔记本
            </button>
          </div>
        </header>

        <section className="home-intro">
          <div>
            <div className="eyebrow-row">
              <span className="eyebrow-dot" />
              <span>{fullDate()} · 秋日书桌</span>
            </div>
            <h1>{greeting()}，{userEmail ? userEmail.split('@')[0] : '林'}<span className="ink-dot">。</span></h1>
            <p>把散落的念头，装订成册。</p>
          </div>
          <div className="shelf-stats" aria-label="书架概览">
            <div><strong>{String(projects.length).padStart(2, '0')}</strong><span>笔记本</span></div>
            <i />
            <div><strong>{totalCards}</strong><span>张卡片</span></div>
            <i />
            <div><strong>{totalWords.toLocaleString('zh-CN')}</strong><span>字</span></div>
          </div>
        </section>

        {featured ? (
          <section className="hero">
            <div className="hero__texture" />
            <div className="hero-copy">
              <div className="hero-kicker">
                <span className="pulse-dot" />
                正在书写
                <span className="hero-kicker__sep">·</span>
                第一稿
              </div>
              <h2>{featured.title}</h2>
              <p className="hero-lead">{featured.description}</p>
              <div className="hero-stats">
                <span><strong>{featured.cards.length}</strong> 张卡片</span>
                <span><strong>{featured.connections.length}</strong> 条关联</span>
                <span><strong>{projectWords(featured).toLocaleString('zh-CN')}</strong> 字</span>
                <span className="hero-progress" style={{ '--hero-progress': featuredProgress + '%' }}><i />{featuredProgress}% 成形</span>
              </div>
              <div className="hero-actions">
                <button className="btn btn--paper" onClick={() => onOpenProject(featured.id, 'board')}>
                  继续书写 <ArrowUpRight size={17} />
                </button>
                <button className="btn btn--hero-ghost" onClick={() => onOpenRoundtable(featured.id)}>
                  <Sparkles size={16} /> 邀请 AI 圆桌
                </button>
              </div>
              <div className="hero-presence">
                <div className="avatar-stack">
                  {AI_ROLES.map((role, index) => (
                    <span key={role.id} className="ai-mini" style={{ background: role.color, zIndex: 6 - index }} title={role.name + ' · ' + role.title}>{role.initials}</span>
                  ))}
                </div>
                <span>创作编辑与学术顾问已就席</span>
                <ArrowRight size={14} />
              </div>
            </div>

            <div className="hero-visual">
              <div className="hero-halo" />
              <div className="paper-stack paper-stack--back" />
              <div className="paper-stack paper-stack--mid" />
              <div className="hero-book-wrap" onClick={() => onOpenProject(featured.id, 'board')} role="button" tabIndex={0} onKeyDown={(event) => { if (event.key === 'Enter') onOpenProject(featured.id, 'board') }}>
                <CoverArt project={featured} variant="hero" />
              </div>
              <div className="hero-note">
                <span className="hero-note__pin" />
                <span className="hero-note__label">叙事线索 / 02</span>
                <p>潮声不是背景音，它是第二个主角。</p>
                <span className="hero-note__meta"><Clock3 size={12} /> 2 小时前 · 手写批注</span>
              </div>
              <div className="hero-stamp">
                <span>ISLE</span>
                <i />
                <span>NO. 03</span>
              </div>
            </div>
          </section>
        ) : null}

        <section className="shelf">
          <div className="shelf-head">
            <div>
              <h3>我的笔记本 <sup>{String(projects.length).padStart(2, '0')}</sup></h3>
              <p>每一本，都是一张可以重新摊开的地图。</p>
            </div>
            <div className="shelf-filters" role="tablist" aria-label="按类型筛选">
              {FILTERS.map((item) => (
                <button
                  key={item.id}
                  role="tab"
                  aria-selected={filter === item.id}
                  className={filter === item.id ? 'filter-chip is-active' : 'filter-chip'}
                  onClick={() => setFilter(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="project-grid">
            {filtered.map((project) => (
              <ProjectTile
                key={project.id}
                project={project}
                featured={project.id === featured?.id}
                onOpen={() => onOpenProject(project.id, 'board')}
              />
            ))}
            <button className="new-notebook" onClick={() => setCreating(true)}>
              <span className="new-notebook__inner">
                <span className="new-notebook__plus"><Plus size={24} /></span>
                <strong>空白的下一本</strong>
                <small>从一张卡片开始</small>
              </span>
            </button>
          </div>

          {filtered.length === 0 ? (
            <div className="shelf-empty">
              <Search size={20} />
              <p>没有找到与“{search}”有关的笔记本。</p>
              <button className="text-button" onClick={() => { setSearch(''); setFilter('all') }}>清空筛选</button>
            </div>
          ) : null}
        </section>

        <footer className="home-foot">
          <span>页屿 PAGE ISLE · 用卡片装订故事</span>
          <div className="home-foot__right">
            <span className="home-platform">Windows · macOS · iPhone · Android</span>
            <button className="home-install" onClick={installApp} disabled={installed}>
              {installed ? <Check size={11} /> : <Download size={11} />}
              {installed ? '已安装' : '安装到设备'}
            </button>
            <span><span className="live-dot" /> {userEmail ? '实时同步至云端 · 版本可回朔' : '自动保存于本地'}</span>
          </div>
        </footer>
      </main>

      {creating ? (
        <NewProjectModal
          onClose={() => setCreating(false)}
          onCreate={(input) => {
            setCreating(false)
            onCreateProject(input)
          }}
        />
      ) : null}
    </div>
  )
}

function ProjectTile({ project, featured, onOpen }) {
  const progress = projectProgress(project)
  const words = projectWords(project)
  return (
    <button className="project-tile" onClick={onOpen}>
      <div className="project-cover-wrap">
        <CoverArt project={project} />
        {featured ? <span className="cover-ribbon">正在书写</span> : null}
        <span className="project-open-hint">打开笔记本 <ArrowUpRight size={15} /></span>
      </div>
      <div className="project-meta">
        <div className="project-meta__top">
          <span>{project.cards.length} 张卡片 · {words} 字</span>
          <span>{relativeTime(project.updatedAt)}</span>
        </div>
        <div className="project-progress-line">
          <span><i style={{ width: progress + '%' }} /></span>
          <em>{progress}%</em>
        </div>
      </div>
    </button>
  )
}
